import React from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants.ts";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { FORM_FIELDS } from "@features/vessel-call/create/constants";
import type { VesselCallOutletContext } from "@features/vessel-call/create/CreateVesselCallContainer";
import { useDocumentUpload } from "@features/vessel-call/create/hooks/useDocumentUpload";
import type { VesselCallFormData } from "@features/vessel-call/create/schema/vesselCallFormSchema";
import { useVesselCallFormStore } from "@features/vessel-call/create/store/useVesselCallForm.store";
import { SasTokenPrefix } from "@services/user/types";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

const TEST_ID_BASE = "fal-form-4";
const LANG_BASE = "vessel-call.create";

const FalForm4: React.FC = () => {
  const { formData, updateFormData } = useOutletContext<VesselCallOutletContext>();
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const documentConditions = {
    ...FILE_UPLOAD_PROFILES.certificates,
    hideErrors: false,
  };

  const formStore = useVesselCallFormStore;

  const crewsEffectsDeclarationDocumentUrl = useDocumentUpload(
    FORM_FIELDS.CREWS_EFFECTS_DECLARATION_DOCUMENT_URL,
    SasTokenPrefix.VESSEL_CALL,
    formData,
    formStore,
  );

  return (
    <div className="p-6 space-y-4">
      <h2 className="text-xl font-medium">{t(`${LANG_BASE}.fal-form-4-title`)}</h2>
      <div className="col-span-full">
        <FormProvider {...formData}>
          <FormSection title={t(`${LANG_BASE}.upload-form`)}>
            <div className="col-span-full">
              <FormItem<
                VesselCallFormData,
                typeof FORM_FIELDS.CREWS_EFFECTS_DECLARATION_DOCUMENT_URL
              >
                name={FORM_FIELDS.CREWS_EFFECTS_DECLARATION_DOCUMENT_URL}
                type="file"
                uploadConditions={documentConditions}
                testId={`${TEST_ID_BASE}-file`}
                files={crewsEffectsDeclarationDocumentUrl.uploadEntries}
                setFiles={(files) => {
                  crewsEffectsDeclarationDocumentUrl.setUploadEntries(files ?? []);
                }}
                onUpload={(_, files) => crewsEffectsDeclarationDocumentUrl.handleUpload(files)}
                onFieldChange={updateFormData}
              />
              {crewsEffectsDeclarationDocumentUrl.uploadEntries.length > 0 && (
                <div className="flex-1">
                  <SingleFile
                    testId={`${TEST_ID_BASE}-document-crews-effects-declaration`}
                    file={crewsEffectsDeclarationDocumentUrl.uploadEntries[0]}
                    isLoading={crewsEffectsDeclarationDocumentUrl.isPending}
                    fieldName={FORM_FIELDS.CREWS_EFFECTS_DECLARATION_DOCUMENT_URL}
                    setFiles={() => crewsEffectsDeclarationDocumentUrl.setUploadEntries([])}
                    removeFn={() => {
                      crewsEffectsDeclarationDocumentUrl.handleRemove();
                    }}
                  />
                </div>
              )}
            </div>
          </FormSection>
        </FormProvider>
      </div>
    </div>
  );
};

export default FalForm4;
